import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SaveOutlined } from '@ant-design/icons';
import DatepickerMultipleField from '@components/common/form/DatepickerMultipleField';
import DropdownField from '@components/common/form/DropdownField';
import TimePickerField from '@components/common/form/TimePickerField';
import { CurrentcyPositions, DATE_FORMAT_DISPLAY, TIME_FORMAT_DISPLAY } from '@constants';
import { currentcyPositions, datetimeFormats, daysOfWeekTimeWork as daysOfWeekTimeWorks } from '@constants/masterData';
import { timeZoneOptions } from '@constants/timezone';
import useTranslate from '@hooks/useTranslate';
import { SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { getDisabledHours, getDisabledMinutes } from '@utils/dayHelper';
import { Button, Col, Flex, Form, Row, Space } from 'antd';
import moment from 'moment';

import '@module-tenant/settings/order/index.scss';

const isMidnight = (value) => !!value && value.hour() === 0 && value.minute() === 0;

const getDisabledMinutesForTo = (hour, fromValue) => {
    const baseDisabled = getDisabledMinutes(hour, fromValue) || [];

    if (
        fromValue &&
        fromValue.hour() === hour &&
        !isMidnight(fromValue)
    ) {
        return [ ...new Set([ ...baseDisabled, fromValue.minute() ]) ];
    }

    return baseDisabled;
};

const parseTimeWorkForForm = (timeWorkRaw) => {
    const result = { dayOffs: [] };
    const hasDayData = timeWorkRaw && daysOfWeekTimeWorks.some((d) => Array.isArray(timeWorkRaw[d.value]));
    if (hasDayData) {
        daysOfWeekTimeWorks.forEach((day) => {
            const frames = timeWorkRaw[day.value] || [];
            result[day.value] = frames.map((item) => {
                const [from, to] = (item || '').split('-');
                return {
                    from: moment(from, TIME_FORMAT_DISPLAY),
                    to: moment(to, TIME_FORMAT_DISPLAY),
                };
            });
        });
        result.dayOffs = timeWorkRaw.dayOffs || [];
    } else {
        daysOfWeekTimeWorks.forEach((day) => {
            result[day.value] = [
                { from: moment('00:00', TIME_FORMAT_DISPLAY), to: moment('00:00', TIME_FORMAT_DISPLAY) },
                { from: moment('00:00', TIME_FORMAT_DISPLAY), to: moment('00:00', TIME_FORMAT_DISPLAY) },
            ];
        });
    }
    return result;
};

const GeneralSettingForm = ({
    formId,
    settingObj,
    restaurantName,
    setIsChangedFormValues,
    onSubmit,
    dataDetail,
}) => {
    const [form] = Form.useForm();
    const translate = useTranslate();
    const daysOfWeekTimeWork = translate.formatKeys(daysOfWeekTimeWorks, ['label']);

    const otherData = {};

    const onValuesChange = () => {
        setIsChangedFormValues(true);
    };

    const onSelectTimezone = (value, item) => {
        otherData.timezone = { name: value, offset: item.other };
        form.setFieldsValue({ timezone: value });
    };

    const onSelectTimeWorkTabletRandom = (fieldName, value) => {
        const time_work = form.getFieldValue('time_work');
        const [ dayKey, dayIndexKey, frameKey ] = fieldName;
        if (frameKey === 'from') {
            const to = time_work[dayKey][dayIndexKey].to;
            // if (to && to.format(TIME_FORMAT_DISPLAY) < value?.format(TIME_FORMAT_DISPLAY)) {
            //     time_work[dayKey][dayIndexKey].to = null;
            // }
            const toIsInvalid =
                to &&
                value &&
                (
                    to.isBefore(value) ||
                    (to.isSame(value) && !isMidnight(value))
                );

            if (toIsInvalid) {
                time_work[dayKey][dayIndexKey].to = null;
            }
        } else if (frameKey === 'to') {
            const from = time_work[dayKey][dayIndexKey].from;
            // if (from && value?.format(TIME_FORMAT_DISPLAY) < from.format(TIME_FORMAT_DISPLAY)) {
            //     value = from;
            // }
            if (from && value) {
                if (value.isBefore(from)) {
                    value = from;
                } else if (value.isSame(from) && !isMidnight(from)) {
                    return;
                }
            }
        }
        time_work[dayKey][dayIndexKey][frameKey] = value;
        form.setFieldsValue({ time_work });
        onValuesChange();
    };

    const handleApplyAll = (e) => {
        e.preventDefault();
        const time_work = form.getFieldValue('time_work');
        const { monday = [] } = time_work;

        for (let { value } of daysOfWeekTimeWork) {
            time_work[value] = monday.map((frame) => ({
                from: moment(frame.from),
                to: moment(frame.to),
            }));
        }

        form.setFieldsValue({ time_work });
        onValuesChange();
    };

    const checkCanApplyAll = () => {
        const time_work = form.getFieldValue('time_work');
        const { monday } = time_work || {};
        if (!monday) return false;
        return monday.every((frame) => !!frame.from && !!frame.to);
    };

    useEffect(() => {
        if (settingObj) {
            form.setFieldsValue({
                email_shop: settingObj.email_shop,
                timezone: settingObj?.timezone?.name || settingObj?.timezone,
                currentcy: settingObj.currentcy,
                currency_code: settingObj.currency_code,
                currentcy_position: settingObj?.currentcy_position
                    ? Number(settingObj.currentcy_position)
                    : CurrentcyPositions.BACK,
                decimal_separator: settingObj.decimal_separator,
                decimal_space: settingObj.decimal_space,
                group_separator: settingObj.group_separator,
                date_time_format: settingObj?.date_time_format || 'dd.MM.yyyy',
                restaurantName: restaurantName,
                imprint: settingObj.imprint,
                time_work: parseTimeWorkForForm(settingObj?.general_time_work),
            });
        }
    }, [settingObj, restaurantName]);

    const handleSubmit = (values) => {
        const originalSettings = dataDetail?.settings || {};
        const selectedTimezone = timeZoneOptions.find((option) => option.name === values?.timezone);
        const updatedGeneral = {
            email_shop: values?.email_shop,
            timezone: {
                name: values?.timezone,
                offset: selectedTimezone?.offset || otherData.timezone?.offset,
            },
            currentcy: values?.currentcy,
            currency_code: values?.currency_code,
            currentcy_position:
                values?.currentcy_position ?? originalSettings.currentcy_position ?? CurrentcyPositions.BACK,
            decimal_separator: values?.decimal_separator,
            decimal_space: values?.decimal_space,
            group_separator: values?.group_separator,
            date_time_format: values?.date_time_format ?? originalSettings.date_time_format ?? 'dd.MM.yyyy',
            imprint: values.imprint,
        };

        const { dayOffs = [], ...timeWorkPerDay } = values?.time_work || {};
        const timeWorkObj = { dayOffs };
        Object.keys(timeWorkPerDay).forEach((key) => {
            timeWorkObj[key] = (timeWorkPerDay[key] || []).map(
                (item) => `${item.from.format('HH:mm')}-${item.to.format('HH:mm')}`,
            );
        });

        const settings = {
            ...originalSettings,
            ...updatedGeneral,
            general_time_work: timeWorkObj,
        };

        const updatedSettings = JSON.stringify(settings);

        const payload = {
            ...dataDetail,
            id: dataDetail.id,
            status: dataDetail.status,
            restaurantName: dataDetail.restaurantName,
            restaurantPath: dataDetail.restaurantPath,
            address: dataDetail.address,
            city: dataDetail.city,
            zipCode: dataDetail.zipCode,
            logoPath: dataDetail.logoPath,
            bannerPath: dataDetail.bannerPath,
            hotline: dataDetail.hotline,
            settings: updatedSettings,
            lang: dataDetail.lang,
            numberOfTable: dataDetail.numberOfTable,
            latitude: dataDetail.latitude,
            longitude: dataDetail.longitude,
        };

        onSubmit(payload);
    };

    const initialValues = {
        email_shop: settingObj?.email_shop,
        timezone: settingObj?.timezone?.name || settingObj?.timezone,
        currentcy: settingObj?.currentcy,
        currency_code: settingObj?.currency_code,
        currentcy_position: settingObj?.currentcy_position
            ? Number(settingObj.currentcy_position)
            : CurrentcyPositions.FRONT,
        decimal_separator: settingObj?.decimal_separator,
        decimal_space: settingObj?.decimal_space,
        group_separator: settingObj?.group_separator,
        date_time_format: settingObj?.date_time_format || 'dd.MM.yyyy',
        restaurantName: restaurantName,
        time_work: parseTimeWorkForForm(settingObj?.general_time_work),
    };

    const renderTimeWork = () => {
        return (
            <table className="happy-hours-table">
                <thead>
                    <tr>
                        <th width="14%">{translate.formatMessage(commonMessage.dayOfWeek)}</th>
                        <th>{translate.formatMessage(commonMessage.timeFrame)}</th>
                    </tr>
                </thead>
                <tbody>
                    {daysOfWeekTimeWork.map((day, dayIndex) => (
                        <tr key={day.value}>
                            <td>{day.label}</td>
                            <td style={{ padding: '10px' }}>
                                <Form.List name={[ 'time_work', day.value ]}>
                                    {(fields) => (
                                        <div className="no-margin-form-item">
                                            <Space className="box-flex" size={24}>
                                                {fields.map((field, index) => (
                                                    <div key={field.key}>
                                                        <div className="frame-label">
                                                            {translate.formatMessage(commonMessage.frameTime)}{' '}
                                                            {index + 1}
                                                        </div>
                                                        <Space className="box-flex">
                                                            <TimePickerField
                                                                size="small"
                                                                fieldName={[ field.name, 'from' ]}
                                                                onChange={(value) =>
                                                                    onSelectTimeWorkTabletRandom(
                                                                        [ day.value, field.name, 'from' ],
                                                                        value,
                                                                    )
                                                                }
                                                                width="100%"
                                                                required
                                                                placeholder={translate.formatMessage(
                                                                    commonMessage.from,
                                                                )}
                                                                requiredMsg={translate.formatMessage(
                                                                    commonMessage.requireFrom,
                                                                )}
                                                                validateTrigger={[ 'onChange', 'onBlur' ]}
                                                                onClick={onValuesChange}
                                                            />
                                                            <TimePickerField
                                                                size="small"
                                                                fieldName={[ field.name, 'to' ]}
                                                                onChange={(value) =>
                                                                    onSelectTimeWorkTabletRandom(
                                                                        [ day.value, field.name, 'to' ],
                                                                        value,
                                                                    )
                                                                }
                                                                width="100%"
                                                                required
                                                                placeholder={translate.formatMessage(commonMessage.to)}
                                                                requiredMsg={translate.formatMessage(
                                                                    commonMessage.requireTo,
                                                                )}
                                                                validateTrigger={[ 'onChange', 'onBlur' ]}
                                                                disabledHours={() => {
                                                                    const timeWork = form.getFieldValue('time_work');
                                                                    const from = timeWork[day.value][field.name].from;
                                                                    return getDisabledHours(from);
                                                                }}
                                                                disabledMinutes={(hour) => {
                                                                    const timeWork = form.getFieldValue('time_work');
                                                                    const from = timeWork[day.value][field.name].from;
                                                                    // return getDisabledMinutes(hour, from);
                                                                    return getDisabledMinutesForTo(hour, from);
                                                                }}
                                                                onClick={onValuesChange}
                                                            />
                                                        </Space>
                                                    </div>
                                                ))}
                                                {!dayIndex && (
                                                    <div className="wrap-btn-apply-all">
                                                        <Button
                                                            disabled={!checkCanApplyAll()}
                                                            type="primary"
                                                            size="small"
                                                            onClick={handleApplyAll}
                                                        >
                                                            {translate.formatMessage(commonMessage.applyAll)}
                                                        </Button>
                                                    </div>
                                                )}
                                            </Space>
                                        </div>
                                    )}
                                </Form.List>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };
    return (
        <Form
            id={formId}
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={initialValues}
            onValuesChange={onValuesChange}
        >
            <Row gutter={16}>
                <Col span={6}>
                    <TextField name="email_shop" label={<FormattedMessage defaultMessage="Email" />} type="email" required rules={[{ type: 'email', message: 'Invalid email format' }]} />
                </Col>
                <Col span={6}>
                    <SelectField
                        onSelect={onSelectTimezone}
                        name="timezone"
                        label={<FormattedMessage defaultMessage="Timezone" />}
                        options={timeZoneOptions}
                        optionOther="offset"
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={6}>
                    <TextField name="currentcy" label={<FormattedMessage defaultMessage="Currency symbol" />} />
                </Col>
                <Col span={6}>
                    <TextField name="currency_code" label={<FormattedMessage defaultMessage="Currency code" />} />
                </Col>
                <Col span={6}>
                    <DropdownField
                        fieldName="currentcy_position"
                        label={<FormattedMessage defaultMessage="Currency Position" />}
                        options={currentcyPositions}
                    />
                </Col>
                <Col span={6}>
                    <TextField name="decimal_separator" label={<FormattedMessage defaultMessage="Decimal Separator" />} />
                </Col>
                <Col span={6}>
                    <TextField name="decimal_space" label={<FormattedMessage defaultMessage="Decimal Space" />} />
                </Col>
                <Col span={6}>
                    <TextField name="group_separator" label={<FormattedMessage defaultMessage="Group Separator" />} />
                </Col>
                <Col span={6}>
                    <DropdownField fieldName="date_time_format" label={<FormattedMessage defaultMessage="Date Time Format" />} options={datetimeFormats} />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <TextField name="imprint" label={<FormattedMessage defaultMessage="Information" />} type="textarea" required rows={3} />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label={translate.formatMessage(commonMessage.timeWork)} style={{ display: 'block' }}>
                        {renderTimeWork()}
                    </Form.Item>
                </Col>
                <Col span={14}>
                    <DatepickerMultipleField
                        label={translate.formatMessage(commonMessage.dayOffs)}
                        fieldName={[ 'time_work', 'dayOffs' ]}
                        format={DATE_FORMAT_DISPLAY}
                    />
                </Col>
            </Row>
            <Flex justify="end">
                <Button key="submit" htmlType="submit" type="primary">
                    <SaveOutlined style={{ marginRight: '6px' }} />
                    {translate.formatMessage(commonMessage.update)}
                </Button>
            </Flex>
        </Form>
    );
};

export default GeneralSettingForm;
